import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';

const navigation = [
  { name: 'Product', href: '/product' },
  { name: 'Work Orders', href: '/work-orders' },
  { name: 'For Pumpers', href: '/pumpers' },
  { name: 'For Operators', href: '/operators' },
  { name: 'Pricing', href: '/pricing' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const condensed = scrolled || mobileMenuOpen;

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-colors duration-300 ease-out ${
        condensed
          ? 'border-b border-hairline bg-roughneck-900/95 backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <Container>
        <nav className="flex h-16 items-center justify-between" aria-label="Main navigation">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo-wordmark.svg"
              alt="Roughneck"
              className="h-4 w-auto sm:h-[18px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-6 lg:flex">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium tracking-tight transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-white'
                    : 'text-roughneck-400 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden items-center gap-5 lg:flex">
            <a
              href="https://app.roughneck.io/login"
              className="text-sm font-medium text-roughneck-400 transition-colors hover:text-white"
            >
              Login
            </a>
            <Button as="anchor" href="https://app.roughneck.io/register" size="sm">
              Register
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded p-2 text-roughneck-300 hover:bg-roughneck-800/60 hover:text-white lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
            <Icon name={mobileMenuOpen ? 'x' : 'menu'} className="h-6 w-6" />
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="border-t border-hairline py-4 lg:hidden"
          >
            <div className="flex flex-col gap-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`rounded px-4 py-3 text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-roughneck-800/60 text-white'
                      : 'text-roughneck-300 hover:bg-roughneck-800/40 hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="mt-4 flex flex-col gap-2 border-t border-hairline pt-4">
                <a
                  href="https://app.roughneck.io/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded px-4 py-3 text-base font-medium text-roughneck-300 hover:bg-roughneck-800/40 hover:text-white"
                >
                  Login
                </a>
                <div className="px-4">
                  <Button as="anchor" href="https://app.roughneck.io/register" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                    Register
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </header>
  );
}
